import React, { useEffect, useState } from "react";
import { useConfirm } from "material-ui-confirm";
import { useDispatch } from 'react-redux';
import { useNavigate } from "react-router-dom";
import { useErrorSetter, useSuccessSetter } from '../../../hooks/useUiMessages';
import AuthHeader from "../../UI/AuthHeader/AuthHeader";
import AuthSideMenu from "../../UI/AuthSideMenu/AuthSideMenu";
import holisticAnalytics from '../../../utils/holisticAnalytics';
// import coolImg from '../../../images/cool.png'
import axios from 'axios';
import OvalSpinner from "../../UI/Spinners/OvalSpinner";
import { checkAuthenticatedUser } from "../../../utils/client-functions";
import { setFetching } from "../../../redux/states";
import { Fetching } from "../../UI/Modal/Modal";
const screenWidth = window.innerWidth



const ReferralHub = () => {
    const errorSetter = useErrorSetter();
    const successSetter = useSuccessSetter();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const confirm = useConfirm();
    const [authMenuOpen, setAuthMenuOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState({ referralLink: '', commissionPercent: 5, referredUsers: [], earnings: { total: 0, currency: 'NGN', entries: [] } });
    const [payout, setPayout] = useState({ provider: 'none', status: 'none', country: '', currency: '', methodType: '', bank: {}, accountName: '', accountNumberMasked: '', payoutsEnabled: false, stripeAccountId: '', requirementsDue: [] });
    const [banks, setBanks] = useState([]);
    const [bankCountry, setBankCountry] = useState('NG');
    const [bankCode, setBankCode] = useState('');
    const [bankAcc, setBankAcc] = useState('');
    const [verifyLoading, setVerifyLoading] = useState(false);
    const [banksLoading, setBanksLoading] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);


    const checkIfAuth = async () => {
        try {
            await checkAuthenticatedUser();
        } catch (error) {
            dispatch(setFetching(false));
            return navigate("/popin?referral-hub");
        }
    };
    // Data-driven Flutterwave supported countries (default NG, GH, KE, UG, TZ)
    const defaultFlwCountries = ['NG','GH','KE','UG','TZ'];
    const configuredFlwCountries = (process.env.REACT_APP_FLW_COUNTRIES || '')
        .split(',')
        .map(c => c.trim().toUpperCase())
        .filter(Boolean);
    const flwCountries = configuredFlwCountries.length ? configuredFlwCountries : defaultFlwCountries;
    const countryNames = { NG: 'Nigeria', GH: 'Ghana', KE: 'Kenya', UG: 'Uganda', TZ: 'Tanzania' };
    const [usdTotal, setUsdTotal] = useState(0);
    const isAuth = localStorage?.getItem('token');


    // Fetch referral data
    useEffect(() => {
        (async () => {
            try {
                await checkIfAuth();
                const origin = window.location.origin;
                const res = await axios.get('/user/referrals', { headers: { 'x-access-token': isAuth, 'x-origin': origin } });
                const d = res?.data || {};
                const computedLink = d.referralLink || (d.referralCode ? `${origin.replace(/\/$/, '')}/join-bubble?ref=${encodeURIComponent(d.referralCode)}` : '');

                setData({
                    referralLink: computedLink,
                    commissionPercent: typeof d.commissionPercent === 'number' ? d.commissionPercent : 5,
                    referredUsers: Array.isArray(d.referredUsers) ? d.referredUsers : [],
                    earnings: d.earnings && typeof d.earnings === 'object' ? {
                        total: typeof d.earnings.total === 'number' ? d.earnings.total : 0,
                        currency: d.earnings.currency || 'NGN',
                        entries: Array.isArray(d.earnings.entries) ? d.earnings.entries : []
                    } : { total: 0, currency: 'NGN', entries: [] }
                });
                // Fetch payout profile
                try {
                    const prof = await axios.get('/user/payout-profile', { headers: { 'x-access-token': isAuth } });
                    setPayout(prof?.data || {});
                } catch (_) {}

                // Fetch exchange rates from cached backend (/api/user-price) and compute USD total from entries
                try {
                    const ex = await axios.get('/api/user-price');
                    const rates = ex?.data?.exchangeRates || null;
                    // compute USD total efficiently: aggregate by currency then convert to USD
                    const byCurr = {};
                    const ent = (d.earnings && Array.isArray(d.earnings.entries)) ? d.earnings.entries : [];
                    for (const e of ent) {
                        const c = (e.currency || 'USD').toUpperCase();
                        const amt = Number(e.commissionAmount || 0);
                        byCurr[c] = (byCurr[c] || 0) + (isFinite(amt) ? amt : 0);
                    }
                    const toUSD = (amount, curr, ratesMap) => {
                        if (!amount) return 0;
                        if (!curr || !ratesMap) return 0;
                        const rUSD = Number(ratesMap.USD || 0);
                        if (curr === 'USD') return amount;
                        const rC = Number(ratesMap[curr] || (curr === 'NGN' ? 1 : 0));
                        if (rC && rUSD) return amount * (rUSD / rC); // c->USD: amount * (NGN->USD / NGN->c)
                        return 0;
                    };
                    let sumUSD = 0;
                    for (const c in byCurr) {
                        sumUSD += toUSD(byCurr[c], c, rates);
                    }
                    setUsdTotal(sumUSD);
                } catch (_) { /* ignore */ }
                setLoading(false);
            } catch (e) {
                setLoading(false);
                errorSetter(e?.response?.data?.message || 'Failed to load referrals');
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(data.referralLink);
            successSetter('Referral link copied');
        } catch (_) {
            errorSetter('Failed to copy');
        }
    };

    const toggleMenu = () => {
        setAuthMenuOpen(!authMenuOpen);
        try { holisticAnalytics.track('ui.feature_used', { featureUsed: 'service_redirect', eventData: { action: 'referralhub.menu_toggle', opened: !authMenuOpen } }); } catch(_){}
    };

    const referredCount = data && Array.isArray(data.referredUsers) ? data.referredUsers.length : 0;
    const safeEarnings = (data && data.earnings) ? data.earnings : { total: 0, currency: 'NGN', entries: [] };
    const entries = Array.isArray(safeEarnings.entries) ? safeEarnings.entries : [];

    // Load banks for Flutterwave countries (NG default)
    useEffect(() => {
        (async () => {
            try {
                setBanksLoading(true);
                const r = await axios.get(`/user/payout/flutterwave/banks?country=${encodeURIComponent(bankCountry)}`, { headers: { 'x-access-token': isAuth } });
                const list = Array.isArray(r?.data?.banks) ? r.data.banks : [];
                // de-duplicate by code/id + name to avoid duplicate option keys
                const seen = new Set();
                const unique = [];
                for (const b of list) {
                    const key = `${b.code || b.id || ''}|${b.name || ''}`;
                    if (!seen.has(key)) { seen.add(key); unique.push(b); }
                }
                setBanks(unique);
            } catch (_) { setBanks([]); }
            finally { setBanksLoading(false); }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bankCountry]);

    const verifyFlutterwaveAccount = async () => {
        try {
            setVerifyLoading(true);
            const selected = banks.find(b => (b.code || b.id) === bankCode);
            const mapCurrency = (c) => {
                const C = String(c || '').toUpperCase();
                if (C === 'NG') return 'NGN';
                if (C === 'GH') return 'GHS';
                if (C === 'KE') return 'KES';
                return '';
            };
            const body = { country: bankCountry, bankCode, bankName: selected?.name || '', accountNumber: bankAcc, currency: mapCurrency(bankCountry) };
            const r = await axios.post('/user/payout/flutterwave/resolve', body, { headers: { 'x-access-token': isAuth } });
            successSetter(`Verified as ${r?.data?.accountName}`);
            const prof = await axios.get('/user/payout-profile', { headers: { 'x-access-token': isAuth } });
            setPayout(prof?.data || {});
        } catch (e) {
            errorSetter(e?.response?.data?.message || 'Failed to verify account');
        } finally {
            setVerifyLoading(false);
        }
    };

    const startStripeOnboarding = async () => {
        try {
            const r = await axios.post('/user/payout/stripe/create-account-link', { country: payout?.country || undefined }, { headers: { 'x-access-token': isAuth } });
            const url = r?.data?.url;
            if (url) window.location.href = url;
        } catch (e) {
            errorSetter(e?.response?.data?.message || 'Failed to start Stripe onboarding');
        }
    };

    const refreshStripeStatus = async () => {
        try {
            const r = await axios.get('/user/payout/stripe/status', { headers: { 'x-access-token': isAuth } });
            if (r?.data?.payoutsEnabled) successSetter('Stripe payouts enabled');
            const prof = await axios.get('/user/payout-profile', { headers: { 'x-access-token': isAuth } });
            setPayout(prof?.data || {});
        } catch (e) {
            errorSetter(e?.response?.data?.message || 'Failed to refresh status');
        }
    };

    const hasPayout = !!(payout && (
        payout.provider && payout.provider !== 'none' && (
            payout.status === 'verified' || payout.payoutsEnabled || payout.accountName || payout.accountNumberMasked
        )
    ));

    const deletePayout = async () => {
        // Ask for confirmation using the shared confirm dialog
        try {
            await confirm({
                title: 'Remove payout details?',
                description: 'You will need to add them again to receive payouts.',
                confirmationText: 'Remove',
                cancellationText: 'Cancel',
                confirmationButtonProps: { color: 'error' }
            });
        } catch {
            // User cancelled
            return;
        }
        try {
            setDeleteLoading(true);
            await axios.delete('/user/payout-profile', { headers: { 'x-access-token': isAuth } });
            setPayout({ provider: 'none', status: 'none', country: '', currency: '', methodType: '', bank: {}, accountName: '', accountNumberMasked: '', payoutsEnabled: false, stripeAccountId: '', requirementsDue: [] });
            setBankCode(''); setBankAcc('');
            successSetter('Payout profile removed');
        } catch (e) {
            errorSetter(e?.response?.data?.message || 'Failed to remove payout profile');
        } finally {
            setDeleteLoading(false);
        }
    };


  return (
    <div className="auth-container">
        {/* For SIDE MENU */}
        <div style={{ width: "100%", padding: "0" }}>
            <div className="auth-bg-blob"></div>
        </div>


        <div className="auth-container-inner">
            {/* for TOP MENU */}
            <AuthHeader 
                headerText="Refer & Earn" 
                authMenuOpen={authMenuOpen}
                onClick={toggleMenu}
            />
            <AuthSideMenu
                opened={authMenuOpen}
                hidden={!authMenuOpen}
                arrayDetails={[]}
            />
            {/* <div style={{margin: '20px auto', width: screenWidth < 900 ? '100%' : '50%'}}>
                <AuthInputs 
                    placeholder="Search for a resume" 
                    inputType="search" 
                    mb={3} 
                    mt={5} 
                    required={true} 
                    value={searchString}
                    onChange={handleSearch}
                />
            </div>  */}

            {loading ? (
                <Fetching />
            ) : (
                <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '20px', alignItems: 'center' }}>
                    {/* Referral contract notice */}
                    <div style={styles.card}>
                        <div style={{ padding: '16px', width: '100%' }}>
                            <h3 style={{ margin: 0 }}>Before you refer</h3>
                            <p style={{ fontSize: '.85rem', marginTop: '6px' }}>
                                Please review our Referral Contract. By using referral links, you acknowledge and agree to those terms.
                            </p>
                            <br />
                            <a
                                href="/privacy#referral-contract"
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 6,
                                    color: '#056b74',
                                    textDecoration: 'none',
                                    fontWeight: 600,
                                    border: '1px solid #056b7450',
                                    padding: '6px 10px',
                                    borderRadius: 8,
                                    background: '#ffffffb0'
                                }}
                                title="Open Referral Contract in a new tab"
                            >
                                Review Referral Contract
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                    <path d="M18 13v6a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                                    <path d="M15 3h6v6"/>
                                    <path d="M10 14L21 3"/>
                                </svg>
                            </a>
                        </div>
                    </div>
                    <div style={styles.card}>
                        <div style={{ padding: '16px', width: '100%' }}>
                            <h3 style={{ margin: 0 }}>Your referral link</h3>
                            <p style={{ fontSize: '.8rem', marginTop: '6px' }}>Earn {data.commissionPercent}% from every subscription your friends make.</p>
                                                        <div style={styles.link} title="Click to copy">
                                                                <input
                                                                    type="text"
                                                                    readOnly
                                                                    value={data.referralLink || ''}
                                                                    onClick={copyLink}
                                                                    style={{
                                                                        flex: 1,
                                                                        border: 'none',
                                                                        color: 'black',
                                                                        background: 'transparent',
                                                                        outline: 'none',
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis',
                                                                        whiteSpace: 'nowrap',
                                                                        fontSize: '.85rem'
                                                                    }}
                                                                />
                                                                <button onClick={copyLink} style={{ border: 'none', background: '#5fbec5', color: 'white', borderRadius: '8px', padding: '6px 10px', cursor: 'pointer' }}>Copy</button>
                                                        </div>
                            <div style={{ display: 'flex', gap: '12px', fontSize: '.8rem', marginTop: '6px' }}>
                                <span><strong>Referred:</strong> {referredCount}</span>
                                <span><strong>Total earned:</strong> {Number(usdTotal || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })} USD</span>
                            </div>
                        </div>
                    </div>
                    <div style={styles.card}>
                        <div style={{ padding: '16px', width: '100%' }}>
                            <h3 style={{ margin: 0 }}>Earnings</h3>
                            {entries.length === 0 ? (
                                <div style={{ fontSize: '.9rem', marginTop: '8px' }}>
                                    <p>No referral earnings yet. As your referrals subscribe, you’ll see earnings here.</p>
                                </div>
                            ) : (
                                <div style={{ marginTop: '10px' }}>
                                    {entries.map((e, idx) => (
                                        <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 0', borderBottom: '1px solid #eaeaea' }}>
                                            <div style={{ fontSize: '.8rem' }}>
                                                <div><strong>{e.subscription}</strong> — {e.duration}</div>
                                                <div style={{ color: '#666' }}>Tx: {e.tx_ref}</div>
                                            </div>
                                            <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
                                                <div style={{ fontWeight: 700 }}>{e.commissionAmount} {e.currency}</div>
                                                <div style={{ fontSize: '.75rem', color: '#000' }}>{new Date(e.createdAt).toLocaleDateString()}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                    <div style={styles.card}>
                        <div style={{ padding: '16px', width: '100%' }}>
                            <h3 style={{ margin: 0 }}>Referred users</h3>
                            {referredCount === 0 ? (
                                <div style={{ fontSize: '.9rem', marginTop: '8px' }}>
                                    <p>You haven’t referred anyone yet. Share your link to start earning.</p>
                                    <p style={{ color: 'black' }}>Please don’t spam— only share with people who’ll benefit from Bubble Ai.</p>
                                </div>
                            ) : (
                                <ul style={{ listStyle: 'none', padding: 0, marginTop: '10px' }}>
                                    {data.referredUsers.map((u) => (
                                        <li key={u.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 0', borderBottom: '1px solid #eaeaea' }}>
                                            <img src={u.profileImg || '/uploads/avatar.png'} alt={u.name} style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover' }} />
                                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                <span style={{ fontWeight: 600, fontSize: '.9rem' }}>{u.name || "*****" + u.email.slice(-11)}</span>
                                                <span style={{ fontSize: '.75rem', color: 'black' }}>****{u.email.slice(-11)}</span>
                                            </div>
                                            <div style={{ marginLeft: 'auto', fontSize: '.75rem', color: 'black' }}>{new Date(u.joinedAt).toLocaleDateString()}</div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                    {/* Payout details section */}
                    <div style={styles.card}>
                        <div style={{ padding: '16px', width: '100%' }}>
                            <h3 style={{ margin: 0 }}>Payout details</h3>
                            {!hasPayout && (
                                <p style={{ fontSize: '.8rem', marginTop: '6px' }}>Add or verify your payout method so we can pay your referral earnings.</p>
                            )}
                            <div style={{ marginTop: 8, fontSize: '.85rem' }}>
                                {hasPayout ? (
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                                        <div>
                                            <div><strong>Status:</strong> {payout.status}{payout.provider !== 'none' ? ` (${payout.provider})` : ''} {payout.payoutsEnabled ? ' • payouts enabled' : ''}</div>
                                            {payout.accountName || payout.accountNumberMasked ? (
                                                <div style={{ marginTop: 4 }}>
                                                    <strong>Bank:</strong> {payout?.bank?.name || '-'} • <strong>Account:</strong> {payout.accountName || '-'} {payout.accountNumberMasked ? `(${payout.accountNumberMasked})` : ''}
                                                </div>
                                            ) : null}
                                            {Array.isArray(payout.requirementsDue) && payout.requirementsDue.length > 0 && (
                                                <div style={{ marginTop: 4 }}>
                                                    <strong>Stripe requirements due:</strong> {payout.requirementsDue.join(', ')}
                                                </div>
                                            )}
                                        </div>
                                        <button type="button" onClick={deletePayout} style={styles.iconBtn} title="Delete payout details" aria-label="Delete payout details" disabled={deleteLoading}>
                                            {deleteLoading ? (
                                                <OvalSpinner size={16} color="#c53030" />
                                            ) : (
                                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#c53030" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <polyline points="3 6 5 6 21 6"/>
                                                    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                                                    <path d="M10 11v6"/>
                                                    <path d="M14 11v6"/>
                                                    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                                                </svg>
                                            )}
                                        </button>
                                    </div>
                                ) : (
                                    <>
                                        <div><strong>Status:</strong> {payout.status}{payout.provider !== 'none' ? ` (${payout.provider})` : ''} {payout.payoutsEnabled ? ' • payouts enabled' : ''}</div>
                                        {payout.accountName || payout.accountNumberMasked ? (
                                            <div style={{ marginTop: 4 }}>
                                                <strong>Bank:</strong> {payout?.bank?.name || '-'} • <strong>Account:</strong> {payout.accountName || '-'} {payout.accountNumberMasked ? `(${payout.accountNumberMasked})` : ''}
                                            </div>
                                        ) : null}
                                        {Array.isArray(payout.requirementsDue) && payout.requirementsDue.length > 0 && (
                                            <div style={{ marginTop: 4 }}>
                                                <strong>Stripe requirements due:</strong> {payout.requirementsDue.join(', ')}
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>

                            {hasPayout ? null : (
                                <>
                                    {/* Flutterwave (Africa) quick verify */}
                                    <div style={{ marginTop: 12 }}>
                                        <h4 style={{ margin: '8px 0' }}>Bank transfer (Flutterwave)</h4>
                                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                            <select value={bankCountry} onChange={(e) => setBankCountry(e.target.value)} style={styles.smallInput} disabled={verifyLoading}>
                                                {flwCountries.map(code => (
                                                    <option key={code} value={code}>{countryNames[code] || code}</option>
                                                ))}
                                            </select>
                                            <select value={bankCode} onChange={(e) => setBankCode(e.target.value)} style={styles.smallInput} disabled={verifyLoading || banksLoading || banks.length === 0}>
                                                <option value="">Select bank</option>
                                                {banks.map((b, idx) => (
                                                    <option key={`${b.code || b.id || 'bank'}-${b.name || ''}-${idx}`} value={b.code || b.id}>{b.name}</option>
                                                ))}
                                            </select>
                                            <input value={bankAcc} onChange={(e) => setBankAcc(e.target.value)} placeholder="Account number" style={styles.smallInput} disabled={verifyLoading || banksLoading || banks.length === 0} />
                                            <button onClick={verifyFlutterwaveAccount} style={styles.primaryBtn} disabled={verifyLoading || banksLoading || banks.length === 0 || !bankCode || !bankAcc}>
                                                {verifyLoading ? <OvalSpinner size={16} color="#fff" /> : 'Verify & Save'}
                                            </button>
                                        </div>
                                        {banksLoading && (
                                            <div style={{ marginTop: 8, fontSize: '.8rem', color: '#555', display: 'flex', alignItems: 'center', gap: 6 }}>
                                                <OvalSpinner size={16} color="#5fbec5" /> Loading banks for {countryNames[bankCountry] || bankCountry}...
                                            </div>
                                        )}
                                        {!banksLoading && banks.length === 0 && (
                                            <div style={{ marginTop: 8, fontSize: '.85rem', color: '#7a5c00', background: '#fff4d4', border: '1px solid #ffe7a3', padding: '8px 10px', borderRadius: 8 }}>
                                                No bank list available for {countryNames[bankCountry] || bankCountry}. You can:
                                                <ul style={{ margin: '6px 0 0 18px' }}>
                                                    <li>Use Stripe Connect (Global) below for payouts.</li>
                                                    <li>Contact support if you prefer mobile money payouts for this region.</li>
                                                </ul>
                                            </div>
                                        )}
                                    </div>

                                    {/* Stripe Connect (global) */}
                                    <div style={{ marginTop: 16 }}>
                                        <h4 style={{ margin: '8px 0' }}>Stripe Connect (Global)</h4>
                                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                            <button onClick={startStripeOnboarding} style={styles.secondaryBtn}>Start onboarding</button>
                                            <button onClick={refreshStripeStatus} style={styles.secondaryBtn}>Refresh status</button>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>


                </div>
            )}


        </div>
    </div>
  );
};

export default ReferralHub;

const styles = {
    cardGrid: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '10px auto'
    },
    card: { 
        backgroundColor: '#c0d1d457',
        borderRadius: '20px',
        color: 'black',
        display: 'flex', 
        width: screenWidth < 900 ? '100%' : '90%',
    },
    buildDate: {
        marginTop: '10px',
        marginBottom: '-10px',
        textAlign: 'right',
        fontSize: '.6rem',
        width: '100%'
    },
    link: {
        borderRadius: '20px',
        color: 'rgba(0, 0, 0, 0.634)',
        display: 'flex',
        alignItems:'center',
        justifyContent: 'space-between',
        backgroundColor: 'rgba(255, 250, 250, 0.625)',
        cursor: 'copy',
        margin: '5px 0',
        height: '35px',
        width: '100%',
        padding: '5px',
        zIndex: '1',
        fontSize: '.75rem',
    },
    img: {
        borderRadius: '50%',
        margin: '10px auto',
        width: '40%',
        maxWidth: '150px',
        maxHeight: '150px'
    },
    greenBtnCont: {
        width: '100px',
        height: '25px'
    },
    noResumes: {
        boxSizing: 'border-box',
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '80vh', 
        width: '100%'
    },
    smallInput: {
        padding: '6px 8px',
        borderRadius: '8px',
        border: '1px solid #ccc',
        background: 'rgba(255,255,255,.9)'
    },
    primaryBtn: {
        border: 'none',
        background: '#5fbec5',
        color: 'white',
        borderRadius: '8px',
        padding: '6px 10px',
        cursor: 'pointer'
    },
    secondaryBtn: {
        border: '1px solid #5fbec5',
        background: 'transparent',
        color: '#5fbec5',
        borderRadius: '8px',
        padding: '6px 10px',
        cursor: 'pointer'
    },
    dangerBtn: {
        border: '1px solid #c53030',
        background: '#fff5f5',
        color: '#c53030',
        borderRadius: '8px',
        padding: '6px 10px',
        cursor: 'pointer'
    },
    iconBtn: {
        width: 36,
        height: 36,
        borderRadius: '50%',
        border: '2px solid #c53030',
        background: 'transparent',
        display: 'grid',
        placeItems: 'center',
        cursor: 'pointer'
    }
}
